<script setup>
import {computed, onMounted, onUnmounted, ref} from 'vue';

/**
 * Offers "Take photo" and "Choose file" as separate controls on a touch device.
 *
 * A single `accept="image/*"` input is not enough: Android Chrome answers an image-only accept
 * with the system photo picker, which has no camera entry at all, so a driver standing next to
 * their car with nothing in the gallery has no way through. The camera is therefore its own input
 * carrying `capture`, which always opens the camera directly.
 *
 * The camera control is hidden on a fine pointer, where `capture` is ignored and the button would
 * open an ordinary file dialog under a misleading label. `pointer: coarse` describes the *primary*
 * pointer, so a tablet with a keyboard attached can report `fine` and lose the camera button — the
 * conservative direction, since "Choose file" still reaches the gallery. It is watched rather than
 * read once so attaching or detaching that keyboard updates the control.
 */
const props = defineProps({
  inputId: {type: String, required: true},
  accept: {type: String, default: 'image/*'},
  disabled: {type: Boolean, default: false},
  multiple: {type: Boolean, default: false},
  cameraLabel: {type: String, default: 'Take photo'},
  fileLabel: {type: String, default: 'Choose file'},
  soleLabel: {type: String, default: ''},
  buttonClass: {type: String, default: 'btn btn-outline-secondary btn-sm'}
});

const emit = defineEmits(['picked']);

const coarsePointer = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(pointer: coarse)')
    : null;

const isTouchDevice = ref(!!coarsePointer?.matches);

function onPointerChange(e) {
  isTouchDevice.value = e.matches;
}

onMounted(() => coarsePointer?.addEventListener('change', onPointerChange));
onUnmounted(() => coarsePointer?.removeEventListener('change', onPointerChange));

const cameraInputId = computed(() => `${props.inputId}-camera`);
const fileInputId = computed(() => `${props.inputId}-file`);

// With no camera control to sit beside, the remaining button keeps the caller's own wording
// ("Upload", "Replace") rather than the two-button "Choose file".
const browseLabel = computed(() =>
    (isTouchDevice.value ? props.fileLabel : (props.soleLabel || props.fileLabel)));

function onChange(event) {
  emit('picked', event);
}
</script>

<template>
  <div class="file-picker-controls d-flex gap-2">
    <template v-if="isTouchDevice">
      <label class="mb-0 text-center" :class="[buttonClass, {disabled: disabled}]" :for="cameraInputId">
        <i class="fa-solid fa-camera me-1"></i>{{ cameraLabel }}
      </label>
      <input :id="cameraInputId" class="d-none" type="file" :accept="accept" capture="environment"
             :disabled="disabled" @change="onChange">
    </template>

    <label class="mb-0 text-center" :class="[buttonClass, {disabled: disabled}]" :for="fileInputId">
      <i class="fa-solid fa-folder-open me-1"></i>{{ browseLabel }}
    </label>
    <input :id="fileInputId" class="d-none" type="file" :accept="accept" :multiple="multiple"
           :disabled="disabled" @change="onChange">
  </div>
</template>

<style scoped>
/* The two controls sit inside a half-width licence tile on a phone, where side by side leaves
   about 70px each and breaks "Take photo" across two lines. A flex basis wider than that tile
   makes them stack there and stay side by side in any roomier container. */
.file-picker-controls {
  flex-wrap: wrap;
}

/* `flex-fill` is deliberately not used on the labels: its `flex: 1 1 auto !important` would win
   over this basis, leaving the wrap point at the labels' own text width. */
.file-picker-controls > label {
  flex: 1 1 7.5rem;
  min-width: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;
}

/* A thumb needs more than the 31px a btn-sm gives it; a mouse does not, and the taller control
   would only make the desktop form heavier. */
@media (pointer: coarse) {
  .file-picker-controls > label {
    min-height: 2.75rem;
  }
}

/* A touch browser leaves :hover applied to the last element tapped, so the control the driver
   just used stays filled and reads as a selected state it isn't. Only the two variants this app
   passes are restated; the theme hard-codes these colours rather than going through the Bootstrap
   button custom properties, so there is nothing generic to inherit from. */
@media (hover: none) {
  .file-picker-controls > label.btn-outline-secondary:hover {
    background-color: var(--white);
    color: var(--theme-secondary);
  }

  .file-picker-controls > label.btn-outline-primary:hover {
    background-color: var(--white);
    color: var(--theme-default);
  }
}
</style>
